import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const TYPE_META = {
  excel:    { icon: '📊', label: 'Excel',        color: '#22c55e' },
  pptx:     { icon: '📑', label: 'Presentation', color: '#2563eb' },
  document: { icon: '📄', label: 'Document',     color: '#f59e0b' },
  html:     { icon: '🖥️',  label: 'Interactive',  color: '#8b5cf6' },
  chart:    { icon: '📈', label: 'Chart',         color: '#06b6d4' },
  code:     { icon: '💻', label: 'Code',          color: '#ec4899' },
  flowchart:{ icon: '🔀', label: 'Flowchart',     color: '#f97316' },
};

export default function Artifacts({ showToast }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { authFetch } = useAuth();
  const [companies, setCompanies]   = useState([]);
  const [selCo, setSelCo]           = useState(location.state?.companyId || '');
  const [artifacts, setArtifacts]   = useState([]);
  const [loading, setLoading]       = useState(false);
  const [filter, setFilter]         = useState('all');
  const [search, setSearch]         = useState('');
  const [deleting, setDeleting]     = useState(null);

  useEffect(() => {
    authFetch('/api/companies').then(r => r.json()).then(d => {
      if (Array.isArray(d)) {
        setCompanies(d);
        if (!selCo && d.length > 0) setSelCo(d[0].id);
      }
    }).catch(() => {});
  }, [authFetch]);

  useEffect(() => {
    if (!selCo) return;
    loadArtifacts(selCo);
  }, [selCo]);

  const loadArtifacts = useCallback(async (cid) => {
    setLoading(true);
    try {
      const res  = await authFetch(`/api/artifacts/${cid}`);
      const data = await res.json();
      if (Array.isArray(data)) setArtifacts(data);
    } catch {}
    setLoading(false);
  }, [authFetch]);

  const deleteArtifact = async (id) => {
    if (!window.confirm('Delete this artifact? This cannot be undone.')) return;
    setDeleting(id);
    const res = await authFetch(`/api/artifacts/${id}`, { method: 'DELETE' });
    if (res.ok) {
      setArtifacts(prev => prev.filter(a => a.id !== id));
      showToast?.('Artifact deleted');
    }
    setDeleting(null);
  };

  const filtered = artifacts.filter(a => {
    if (filter !== 'all' && a.artifact_type !== filter) return false;
    if (search && !a.name.toLowerCase().includes(search.toLowerCase()) &&
        !(a.description||'').toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const types = [...new Set(artifacts.map(a => a.artifact_type))];

  const fmtDate = (dt) => {
    try { return new Date(dt + (dt.includes('Z') ? '' : 'Z')).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' }); }
    catch { return dt?.split('T')?.[0] || ''; }
  };

  const fmtSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024*1024) return `${Math.round(bytes/1024)} KB`;
    return `${(bytes/1024/1024).toFixed(1)} MB`;
  };

  return (
    <div className="page-wrap">
      {/* Header */}
      <div className="page-header" style={{ marginBottom: 24 }}>
        <div>
          <div className="page-title">Artifact Repository</div>
          <div className="page-sub">All AI-generated files, dashboards, and artifacts — organized by company</div>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/ai')}>
          ✦ Create New Artifact
        </button>
      </div>

      {/* Company Selector */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <select className="form-select" style={{ maxWidth: 280 }} value={selCo} onChange={e => setSelCo(e.target.value)}>
          <option value="">Select company...</option>
          {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <input
          className="form-input"
          placeholder="Search artifacts..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ maxWidth: 240 }}
        />
        {/* Type filter pills */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {['all', ...types].map(t => {
            const m = TYPE_META[t] || { icon: '📦', label: t };
            return (
              <button
                key={t}
                onClick={() => setFilter(t)}
                style={{
                  padding: '4px 12px', borderRadius: 20, fontSize: 12, cursor: 'pointer',
                  border: `1px solid ${filter === t ? 'var(--accent)' : 'var(--border)'}`,
                  background: filter === t ? 'var(--blue-soft)' : 'transparent',
                  color: filter === t ? 'var(--accent)' : 'var(--text-3)',
                  fontWeight: filter === t ? 600 : 400,
                }}>
                {t === 'all' ? `All (${artifacts.length})` : `${m.icon} ${m.label}`}
              </button>
            );
          })}
        </div>
      </div>

      {/* Empty state */}
      {!selCo && (
        <div className="card" style={{ textAlign: 'center', padding: 60 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📦</div>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Select a company to view artifacts</div>
          <div style={{ fontSize: 13, color: 'var(--text-3)' }}>All AI-generated files are organized by company</div>
        </div>
      )}

      {selCo && loading && (
        <div className="card" style={{ textAlign: 'center', padding: 60 }}>
          <div className="loader" style={{ margin: '0 auto' }} />
        </div>
      )}

      {selCo && !loading && filtered.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: 60 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>✦</div>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>
            {artifacts.length === 0 ? 'No artifacts yet' : 'No artifacts match your search'}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-3)', marginBottom: 20 }}>
            {artifacts.length === 0
              ? 'Visit the AI Workspace and ask for Excel models, presentations, or dashboards — they\'ll all appear here.'
              : 'Try adjusting your search or filter'}
          </div>
          {artifacts.length === 0 && (
            <button className="btn btn-primary" onClick={() => navigate('/ai', { state: { companyId: selCo } })}>
              ✦ Open AI Workspace
            </button>
          )}
        </div>
      )}

      {/* Artifacts grid */}
      {filtered.length > 0 && (
        <div className="g3" style={{ gap: 14 }}>
          {filtered.map(art => {
            const m = TYPE_META[art.artifact_type] || { icon: '📦', label: art.artifact_type, color: 'var(--accent)' };
            return (
              <div key={art.id} className="card" style={{ borderLeft: `3px solid ${m.color}`, padding: 16 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
                  <div style={{ fontSize: 22, flexShrink: 0 }}>{m.icon}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)', lineHeight: 1.3, marginBottom: 3 }}>
                      {art.name}
                    </div>
                    {art.description && (
                      <div style={{ fontSize: 11.5, color: 'var(--text-3)', lineHeight: 1.4 }}>
                        {art.description}
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <span style={{
                    fontSize: 10, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase',
                    color: m.color, background: `${m.color}18`, borderRadius: 4, padding: '2px 8px',
                  }}>{m.label}</span>
                  {art.file_size > 0 && (
                    <span style={{ fontSize: 11, color: 'var(--text-4)' }}>{fmtSize(art.file_size)}</span>
                  )}
                  <span style={{ fontSize: 11, color: 'var(--text-4)', marginLeft: 'auto' }}>
                    {fmtDate(art.created_at)}
                  </span>
                </div>

                <div style={{ display: 'flex', gap: 8 }}>
                  {art.download_url && (
                    <a
                      href={art.download_url}
                      target="_blank" rel="noreferrer"
                      className="btn btn-primary btn-sm"
                      style={{ fontSize: 12, textDecoration: 'none' }}
                    >
                      ↓ Download
                    </a>
                  )}
                  <button
                    className="btn btn-ghost btn-sm"
                    style={{ fontSize: 12 }}
                    onClick={() => navigate('/ai', { state: { companyId: selCo } })}
                  >
                    ✦ Refine in AI
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    style={{ fontSize: 12, marginLeft: 'auto', color: deleting === art.id ? 'var(--red)' : 'var(--text-4)', opacity: deleting === art.id ? 0.6 : 1 }}
                    onClick={() => deleteArtifact(art.id)}
                    disabled={deleting === art.id}
                  >
                    {deleting === art.id ? '…' : '✕'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
